import { useCallback, useEffect, useRef, useState } from "react";
import { getClientStats } from "@/features/clients/api";
import type { ClientStats } from "@/features/clients/types";
import { fetchArSummary } from "@/features/invoices/api";
import type { ArSummary } from "@/features/invoices/types";
import { getESignatureSummary } from "@/features/esignatures/api";
import { getPendingPoas } from "@/features/eservices/api";
import { countPendingNotices } from "@/features/notices/api";

export interface DashboardData {
  clients: ClientStats | null;
  pendingNotices: number | null;
  ar: ArSummary | null;
  signaturesAwaiting: number | null;
  pendingPoas: number | null;
}

interface DashboardStatsState {
  data: DashboardData | null;
  loading: boolean;
  /** Set only when the anchor client-stats read fails — the KPIs can't render
   * meaningfully without it. Individual secondary counts degrade to `null`. */
  error: string | null;
  refetch: () => void;
}

/**
 * Loads every figure the Control Center dashboard shows — client counts, open
 * cases, pending IRS notices, outstanding A/R, e-signatures awaiting, and
 * pending POAs — from their firm-scoped endpoints in parallel. Each source is
 * fetched independently and tolerated on failure, so one flaky endpoint (e.g.
 * e-services) degrades a single card rather than blanking the whole dashboard.
 *
 * `firm` (from useFirmContext) scopes every figure to the active workspace so the
 * KPIs follow a house user who has switched into a Business Practitioner — the
 * whole dashboard re-scopes together (no card is left on the house book).
 */
export function useDashboardStats(firm?: string): DashboardStatsState {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request id so a slow response from an earlier load()/refetch()
  // can never clobber the state of a newer one (e.g. rapid Retry clicks).
  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    Promise.all([
      getClientStats(firm).catch(() => null),
      countPendingNotices(firm).catch(() => null),
      fetchArSummary(false, firm).catch(() => null),
      getESignatureSummary({ scope: "firm", firm })
        .then((s) => s.totalPending)
        .catch(() => null),
      getPendingPoas(firm)
        .then((rows) => rows.length)
        .catch(() => null),
    ]).then(([clients, pendingNotices, ar, signaturesAwaiting, pendingPoas]) => {
      if (requestId !== requestIdRef.current) return;
      setData({ clients, pendingNotices, ar, signaturesAwaiting, pendingPoas });
      if (!clients) setError("Couldn't reach the server. Check your connection and retry.");
      setLoading(false);
    });
  }, [firm]);

  useEffect(() => {
    load();
    // Invalidate any in-flight request on unmount so its late resolution is a no-op.
    return () => {
      requestIdRef.current++;
    };
  }, [load]);

  return { data, loading, error, refetch: load };
}
